import { MEDIA_URL } from './constants';

export const getImageUrl = (imagePath) => {
  if (!imagePath) return 'https://via.placeholder.com/400x300?text=No+Image';
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
  if (imagePath.startsWith('/')) return `${MEDIA_URL}${imagePath}`;
  return `${MEDIA_URL}/${imagePath}`;
};

export const renderStars = (rating) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (i <= Math.floor(rating)) {
      stars.push(<span key={i} className="star filled">★</span>);
    } else if (i === Math.ceil(rating) && rating % 1 !== 0) {
      stars.push(<span key={i} className="star half">★</span>);
    } else {
      stars.push(<span key={i} className="star empty">★</span>);
    }
  }
  return stars;
};

export const transformAdData = (ad, getImageUrlFn) => {
  console.log('🔍 helpers.js - Raw ad data:', ad);

  // ✅ If data is already fully transformed by ApiService (has absolute HTTP URLs
  // in images array), return as-is to avoid double-transformation.
  // This covers both image-only and mixed image+video ads.
  if (ad.images && Array.isArray(ad.images) && ad.images.length > 0 &&
      typeof ad.images[0] === 'string' && ad.images[0].startsWith('http')) {
    console.log('✅ helpers.js - Already transformed by ApiService, returning as-is');
    return ad;
  }

  // ── Legacy path: data NOT yet transformed by ApiService ──────────────────
  const resolveUrl = getImageUrlFn || getImageUrl;
  let allImages = [];

  if (ad.images && Array.isArray(ad.images) && ad.images.length > 0) {
    allImages = ad.images.map(img => {
      if (typeof img === 'string') return img.startsWith('http') ? img : resolveUrl(img);
      const path = img.image || img.image_url || img.url || img.path;
      return path ? resolveUrl(path) : null;
    }).filter(Boolean);
  }

  if (allImages.length === 0 && ad.ad_images && Array.isArray(ad.ad_images)) {
    allImages = ad.ad_images.map(img => {
      if (typeof img === 'string') return resolveUrl(img);
      const path = img.image || img.image_url || img.url || img.path;
      return path ? resolveUrl(path) : null;
    }).filter(Boolean);
  }

  // ✅ Include videos in the combined media array (same as api.js does)
  let allVideos = [];
  if (ad.videos && Array.isArray(ad.videos) && ad.videos.length > 0) {
    allVideos = ad.videos.map(v => {
      if (typeof v === 'string') return v.startsWith('http') ? v : resolveUrl(v);
      const path = v.video_url || v.video || v.url;
      return path ? (path.startsWith('http') ? path : resolveUrl(path)) : null;
    }).filter(Boolean);
  }
  if (ad.primary_video && !allVideos.includes(ad.primary_video)) {
    allVideos.unshift(ad.primary_video);
  }

  const allMedia = [...allImages, ...allVideos];

  let primaryImageUrl = 'https://via.placeholder.com/400x300?text=No+Image';
  if (ad.primary_image) {
    primaryImageUrl = resolveUrl(ad.primary_image);
  } else if (ad.img) {
    primaryImageUrl = ad.img;
  } else if (allImages.length > 0) {
    primaryImageUrl = allImages[0];
  } else if (allVideos.length > 0) {
    primaryImageUrl = allVideos[0]; // use video as thumbnail if no image
  }

  if (primaryImageUrl !== 'https://via.placeholder.com/400x300?text=No+Image' &&
      !allMedia.includes(primaryImageUrl)) {
    allMedia.unshift(primaryImageUrl);
  }

  console.log('✅ helpers.js - Transformed media (images+videos):', allMedia);

  // ✅ FIX: Resolve actual rating from all possible backend field names.
  // Checks every common field the Django serializer might return before
  // falling back to null (never hardcode 3.5 — that hides real data).
  const resolvedRating = (() => {
    const raw =
      ad.average_rating ??   // annotated queryset field (most common in DRF)
      ad.avg_rating ??        // alternative annotation name
      ad.seller_rating ??     // denormalised seller rating stored on ad
      ad.rating ??            // generic field name
      ad.stars ??             // some serializers use this
      null;
    if (raw === null || raw === undefined) return null;
    const parsed = parseFloat(raw);
    return isNaN(parsed) ? null : parsed;
  })();

  return {
    id: ad.id,
    name: ad.name || ad.title,
    title: ad.title || ad.name,
    price: typeof ad.price === 'number' ? ad.price : parseFloat(ad.price),
    category: ad.category || ad.category_name || 'Uncategorized',
    img: primaryImageUrl,
    city: ad.city || 'Not specified',
    location: ad.location || ad.city || 'Not specified',
    verified: ad.verified || ad.user_verified || false,
    // ✅ Use real rating from DB; null means "no ratings yet" — render 0 stars
    rating: resolvedRating,
    description: ad.description || '',
    contact: ad.contact || ad.phone || '',
    images: allMedia,   // ✅ images + videos combined
    videos: allVideos,  // ✅ videos-only list
    seller: ad.seller || ad.sellerName || ad.seller_name || 'Anonymous Seller',
    sellerName: ad.sellerName || ad.seller || ad.seller_name || 'Anonymous Seller',
    sellerPhone: ad.sellerPhone || ad.seller_phone || '',
    sellerEmail: ad.sellerEmail || ad.seller_email || '',
    phone: ad.phone || ad.sellerPhone || ad.seller_phone || '',
    email: ad.email || ad.sellerEmail || ad.seller_email || '',
    views: ad.views || 0,
    condition: ad.condition || 'New',
    postedTime: ad.postedTime || '2 hours ago',
    createdAt: ad.createdAt || ad.created_at,
    isFeatured: ad.isFeatured || ad.is_featured || false,
    sellerJoined: ad.sellerJoined || ad.seller_joined || 'Jan 2024',
    sellerListings: ad.sellerListings || ad.seller_listings || 12,
  };
};

export const transformJobData = (job) => ({
  id: job.id,
  title: job.title,
  company: job.company,
  salaryMin: job.salary_min,
  salaryMax: job.salary_max,
  salaryType: job.salary_type || 'year',
  icon: job.icon || '💼',
  iconBgColor: job.icon_bg_color || '#e3f2fd'
});











// import { MEDIA_URL } from './constants';

// export const getImageUrl = (imagePath) => {
//   if (!imagePath) return 'https://via.placeholder.com/400x300?text=No+Image';
//   if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
//   if (imagePath.startsWith('/')) return `${MEDIA_URL}${imagePath}`;
//   return `${MEDIA_URL}/${imagePath}`;
// };

// export const renderStars = (rating) => {
//   const stars = [];
//   for (let i = 1; i <= 5; i++) {
//     if (i <= Math.floor(rating)) {
//       stars.push(<span key={i} className="star filled">★</span>);
//     } else if (i === Math.ceil(rating) && rating % 1 !== 0) {
//       stars.push(<span key={i} className="star half">★</span>);
//     } else {
//       stars.push(<span key={i} className="star empty">★</span>);
//     }
//   }
//   return stars;
// };

// export const transformAdData = (ad, getImageUrl) => {
//   console.log('🔍 helpers.js - Raw ad data:', ad);
  
//   // ✅ FIX: If data is already fully transformed by ApiService, just return it
//   // ApiService.transformAdData already resolves all URLs properly
//   if (ad.images && Array.isArray(ad.images) && ad.images.length > 0 && ad.images[0].startsWith('http')) {
//     console.log('✅ helpers.js - Data already transformed by ApiService, returning as-is');
//     return ad;
//   }
  
//   // Get all images from the backend (legacy path for data not from ApiService)
//   let allImages = [];
  
//   // Handle different possible image array formats
//   if (ad.images && Array.isArray(ad.images) && ad.images.length > 0) {
//     allImages = ad.images.map(img => {
//       if (typeof img === 'string') {
//         // If already a URL string, use it directly (already transformed)
//         return img.startsWith('http') ? img : getImageUrl(img);
//       }
//       // Handle object with various possible property names
//       const imagePath = img.image || img.image_url || img.url || img.path;
//       return imagePath ? getImageUrl(imagePath) : null;
//     }).filter(Boolean); // Remove any null values
//   }
  
//   // If no images in the array, try to get from ad_images property
//   if (allImages.length === 0 && ad.ad_images && Array.isArray(ad.ad_images)) {
//     allImages = ad.ad_images.map(img => {
//       if (typeof img === 'string') {
//         return getImageUrl(img);
//       }
//       const imagePath = img.image || img.image_url || img.url || img.path;
//       return imagePath ? getImageUrl(imagePath) : null;
//     }).filter(Boolean);
//   }
  
//   // Set primary image (first image or placeholder)
//   let primaryImageUrl = 'https://via.placeholder.com/400x300?text=No+Image';
//   if (ad.primary_image) {
//     primaryImageUrl = getImageUrl(ad.primary_image);
//   } else if (ad.img) {
//     // If img is already set (from previous transformation), use it
//     primaryImageUrl = ad.img;
//   } else if (allImages.length > 0) {
//     primaryImageUrl = allImages[0];
//   }
  
//   // Add primary image to allImages if it's not already there
//   if (primaryImageUrl !== 'https://via.placeholder.com/400x300?text=No+Image' && !allImages.includes(primaryImageUrl)) {
//     allImages.unshift(primaryImageUrl);
//   }
  
//   console.log('✅ helpers.js - Transformed images:', allImages);
  
//   // Return the ad data, preserving already-transformed fields
//   const transformed = {
//     id: ad.id,
//     name: ad.name || ad.title,
//     title: ad.title || ad.name,
//     price: typeof ad.price === 'number' ? ad.price : parseFloat(ad.price),
//     category: ad.category || ad.category_name || 'Uncategorized',
//     img: primaryImageUrl,
//     city: ad.city || 'Not specified',
//     location: ad.location || ad.city || 'Not specified',
//     verified: ad.verified || ad.user_verified || false,
//     rating: ad.rating || 3.5,
//     description: ad.description || '',
//     contact: ad.contact || ad.phone || '',
//     images: allImages, // Include ALL images
//     seller: ad.seller || ad.sellerName || ad.seller_name || 'Anonymous Seller',
//     sellerName: ad.sellerName || ad.seller || ad.seller_name || 'Anonymous Seller',
//     sellerPhone: ad.sellerPhone || ad.seller_phone || '',
//     sellerEmail: ad.sellerEmail || ad.seller_email || '',
//     phone: ad.phone || ad.sellerPhone || ad.seller_phone || '',
//     email: ad.email || ad.sellerEmail || ad.seller_email || '',
//     views: ad.views || 0,
//     condition: ad.condition || 'New',
//     postedTime: ad.postedTime || '2 hours ago',
//     createdAt: ad.createdAt || ad.created_at,
//     isFeatured: ad.isFeatured || ad.is_featured || false,
//     sellerJoined: ad.sellerJoined || ad.seller_joined || 'Jan 2024',
//     sellerListings: ad.sellerListings || ad.seller_listings || 12,
//   };
  
//   console.log('✅ helpers.js - Final transformed category:', transformed.category);
  
//   return transformed;
// };

// export const transformJobData = (job) => ({
//   id: job.id,
//   title: job.title,
//   company: job.company,
//   salaryMin: job.salary_min,
//   salaryMax: job.salary_max,
//   salaryType: job.salary_type || 'year',
//   icon: job.icon || '💼',
//   iconBgColor: job.icon_bg_color || '#e3f2fd'
// });